import React, { useState, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { CaptionSemibold } from "storybook-components/Typography";
import HubCardLink from "./HubCardLink";
import Arrow from "../../../images/arrow-right.svg";

const HubCardListContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 96px;
`;

const CardLinksContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  transition: max-height ease ${({ theme }) => theme.motion.medium},
    height ease ${({ theme }) => theme.motion.medium};
  height: 800px;
  max-height: ${({ overflow }) => `${overflow}px`};
  ${({ theme }) => theme.media.tabletUp`
    height: unset;
    max-height: unset;
  `};
  ${({ theme }) => theme.media.desktopUp`
    height: 800px;
    max-height: ${({ overflow }) => `${overflow}px`};
  `};
`;

const PagingContainer = styled.div`
  display: flex;
  margin: 40px auto 0;
  ${({ theme }) => theme.media.tabletUp`
    margin: 104px auto 0;
  `};
`;

const PageButton = styled.button`
  cursor: pointer;
  width: 32px;
  height: 32px;
  margin: auto 6px;
  border-radius: 4px;
  border: solid 1px ${({ theme }) => theme.colors.gray1};
  color: ${({ theme }) => theme.colors.gray1};
  background-color: transparent;
  &:hover {
    color: ${({ theme }) => theme.colors.green2};
  }
  ${(props) =>
    props.active &&
    css`
      background-color: ${({ theme }) => theme.colors.green2};
      border-color: ${({ theme }) => theme.colors.green2};
      color: ${({ theme }) => theme.colors.green5};
      &:hover {
        color: ${({ theme }) => theme.colors.green5};
      }
    `}
`;

const PageButtonText = styled(CaptionSemibold)`
  color: inherit;
  margin: auto;
  line-height: 100%;
`;

const PageDirectionButton = styled.button`
  width: 32px;
  height: 32px;
  margin: auto 24px;
  border-radius: 4px;
  cursor: default;
  background-color: ${({ theme }) => theme.colors.gray8};
  border: none;
  &:focus,
  &:active {
    outline: none;
  }
  &:hover {
    background-color: ${({ theme }) => theme.colors.green2};
  }
  ${(props) =>
    props.enabled &&
    css`
      background-color: ${({ theme }) => theme.colors.gray1};
      cursor: pointer;
      &:active {
        box-shadow: 0px 5px 6px 0px #1919250f;
      }
    `}
  ${(props) =>
    props.back &&
    css`
      transform: rotateY(180deg);
    `}
`;

const HubCardList = ({ showAll, isDesktopView, links }) => {
  const cardContainer = useRef(null);
  const [currentLinks, setLinks] = useState(links.concat());
  const [overflow, setOverflow] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  let cardsPerPage = 3;
  if (isDesktopView) {
    cardsPerPage = 9;
  }
  const numPages = Math.ceil(currentLinks.length / cardsPerPage);
  const pages = Array(numPages).fill();
  const pageCards = pages.map((page, index) => {
    return currentLinks.slice(
      index * cardsPerPage,
      index * cardsPerPage + cardsPerPage
    );
  });
  // update the overflow and links
  useEffect(() => {
    setCurrentPage(0);
    setLinks(links);
    setOverflow(
      links.length < cardsPerPage ? 216 * Math.ceil(links.length / 3) : 664
    );
  }, [showAll, isDesktopView, links]);

  // if isDesktopView prop has not been set, don't render anything
  if (isDesktopView === undefined) {
    return null;
  }
  const backButtonEnabled = currentPage > 0;
  const forwardButtonEnabled = currentPage < pages.length - 1;

  return (
    <HubCardListContainer>
      <CardLinksContainer
        ref={cardContainer}
        showAll={showAll}
        overflow={overflow}
      >
        {pageCards[currentPage].map((linkObj) => (
          <HubCardLink link={linkObj} key={linkObj.name} />
        ))}
      </CardLinksContainer>
      <PagingContainer>
        {pages.length > 1 && (
          <PageDirectionButton
            back
            enabled={backButtonEnabled}
            onClick={() =>
              setCurrentPage(backButtonEnabled ? currentPage - 1 : currentPage)
            }
          >
            <img
              src={Arrow}
              alt={
                backButtonEnabled
                  ? "previous page button icon"
                  : "disabled previous page button icon"
              }
            />
          </PageDirectionButton>
        )}
        {isDesktopView &&
          pageCards.length > 1 &&
          pageCards.map((page, index) => {
            // Use the first page's link dest as button key to avoid using index
            const key = page[0]?.to;
            return (
              <PageButton
                active={currentPage === index}
                key={key}
                onClick={() => setCurrentPage(index)}
              >
                <PageButtonText>{index + 1}</PageButtonText>
              </PageButton>
            );
          })}
        {pages.length > 1 && (
          <PageDirectionButton
            enabled={forwardButtonEnabled}
            onClick={() =>
              setCurrentPage(
                forwardButtonEnabled ? currentPage + 1 : currentPage
              )
            }
          >
            <img
              src={Arrow}
              alt={
                forwardButtonEnabled
                  ? "next page button icon"
                  : "disabled next page button icon"
              }
            />
          </PageDirectionButton>
        )}
      </PagingContainer>
    </HubCardListContainer>
  );
};

export default HubCardList;
